import { useState, useEffect, useCallback } from "react";
import characterSprite from "@/assets/character-sprite.png";

// Интерфейс для позиции персонажа
interface Position {
  x: number;  // Координата X
  y: number;  // Координата Y
}

export const Character = ({ position, onMove }: { position: Position; onMove: (newPosition: Position) => void }) => {
  const [keys, setKeys] = useState<Set<string>>(new Set());  // Состояние нажатых клавиш

  const moveSpeed = 5;  // Скорость движения персонажа

  // Обработчик нажатия клавиш
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const key = event.key.toLowerCase();
    if (['w', 'a', 's', 'd'].includes(key)) {  // Проверяем клавиши WASD
      event.preventDefault();  // Предотвращаем стандартное поведение
      setKeys(prev => new Set(prev.add(key)));  // Добавляем клавишу в набор
    }
  }, []);

  // Обработчик отпускания клавиш
  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    const key = event.key.toLowerCase();
    if (['w', 'a', 's', 'd'].includes(key)) {  // Проверяем клавиши WASD
      event.preventDefault();  // Предотвращаем стандартное поведение
      setKeys(prev => {
        const newKeys = new Set(prev);
        newKeys.delete(key);  // Удаляем клавишу из набора
        return newKeys;
      });
    }
  }, []);

  // Подписка на события клавиатуры
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Отписка при размонтировании компонента
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Обработка движения персонажа
  useEffect(() => {
    if (keys.size === 0) return;  // Если нет нажатых клавиш, не двигаемся

    const moveInterval = setInterval(() => {
      let newX = position.x;
      let newY = position.y;

      if (keys.has('w')) newY -= moveSpeed; // Движение вверх
      if (keys.has('s')) newY += moveSpeed; // Движение вниз
      if (keys.has('a')) newX -= moveSpeed; // Движение влево
      if (keys.has('d')) newX += moveSpeed; // Движение вправо

      // Удерживаем персонажа в границах экрана
      const maxX = window.innerWidth - 80;
      const maxY = window.innerHeight - 80;
      
      newX = Math.max(0, Math.min(maxX, newX));  // Ограничиваем по X
      newY = Math.max(0, Math.min(maxY, newY));  // Ограничиваем по Y

      // Обновляем позицию только если она изменилась
      if (newX !== position.x || newY !== position.y) {
        onMove({ x: newX, y: newY });
      }
    }, 16); // ~60 FPS

    return () => clearInterval(moveInterval);
  }, [keys, position, onMove, moveSpeed]);

  return (
    <div
      className="absolute transition-all duration-75 ease-linear z-20"
      style={{ 
        left: position.x, 
        top: position.y,
        transform: 'translateZ(0)' // Принудительное аппаратное ускорение
      }}
    >
      <div className="relative">
        <img 
          src={characterSprite} 
          alt="Character" 
          className="w-16 h-16 drop-shadow-lg"
        />
        {/* Эффект свечения персонажа */}
        <div className="absolute inset-0 w-16 h-16 bg-accent/20 rounded-full blur-sm -z-10" />
      </div>
    </div>
  );
};
